import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import dayjs from "dayjs";

import { Button, Chip, DateTimeInput, SpotPicker } from "@/ui";
import { useSpots } from "@/features/queries";
import {
  useCreatePlayRequest,
  usePlayState,
  type PlayMode,
  type TimeMode,
} from "@/features/play/api";
import { BackHeader, ErrorBox, Field } from "@/features/play/parts";
import { SpotMapPicker } from "@/features/play/SpotMapPicker";

const MAX_SPOTS = 5;
const MSK_OFFSET_H = 3;

/** Tomorrow 19:00 Moscow time (= 16:00 UTC), whatever the device zone. */
function tomorrowAt19Msk(): string {
  const msk = new Date(Date.now() + MSK_OFFSET_H * 3600_000);
  return new Date(
    Date.UTC(msk.getUTCFullYear(), msk.getUTCMonth(), msk.getUTCDate() + 1, 19 - MSK_OFFSET_H),
  ).toISOString();
}

/** «Сегодня вечером» = 18:00–23:00 МСК; offered while there's time to match. */
function eveningStillPossible(): boolean {
  const mskHour = (new Date().getUTCHours() + MSK_OFFSET_H) % 24;
  return mskHour < 22;
}

export function PlaySearchPage() {
  const navigate = useNavigate();
  const state = usePlayState();
  const { data: spots = [] } = useSpots();
  const create = useCreatePlayRequest();

  const [mode, setMode] = useState<PlayMode>("casual");
  // First pick is the main spot; any more are «тоже подходит».
  const [spotIds, setSpotIds] = useState<number[]>([]);
  const [timeMode, setTimeMode] = useState<TimeMode>(eveningStillPossible() ? "evening" : "any");
  const [exactAt, setExactAt] = useState<string | null>(tomorrowAt19Msk);
  const [group, setGroup] = useState(true);
  const [spotView, setSpotView] = useState<"list" | "map">("list");

  const toggleSpot = (id: number) =>
    setSpotIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : ids.length < MAX_SPOTS ? [...ids, id] : ids,
    );

  // Shops are on the map, but nobody plays there.
  const playable = useMemo(() => spots.filter((s) => s.place_type !== "store"), [spots]);
  const selected = useMemo(
    () => spotIds.map((id) => playable.find((s) => s.id === id)).filter((s) => s !== undefined),
    [spotIds, playable],
  );

  if (state.data?.request) return <Navigate to="/play" replace />;

  // Re-checked on every render: a form left open past 22:00 МСК must not
  // keep submitting «сегодня вечером».
  const eveningOk = eveningStillPossible();
  const canSubmit =
    spotIds.length > 0 &&
    (timeMode !== "exact" || exactAt !== null) &&
    (timeMode !== "evening" || eveningOk) &&
    !create.isPending;

  const submit = async () => {
    if (spotIds.length === 0) return;
    try {
      await create.mutateAsync({
        spot_ids: spotIds,
        mode,
        time_mode: timeMode,
        exact_at: timeMode === "exact" ? exactAt : null,
        open_to_group: mode === "casual" && group,
      });
      navigate("/play", { replace: true });
    } catch {
      /* shown below */
    }
  };

  return (
    <div style={{ paddingBottom: 24 }}>
      <BackHeader title="Найти соперника" onBack={() => navigate(-1)} />

      <Field label="Как играем">
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            fullWidth
            size="sm"
            variant={mode === "casual" ? "primary" : "secondary"}
            onClick={() => setMode("casual")}
          >
            Просто поиграть
          </Button>
          <Button
            fullWidth
            size="sm"
            variant={mode === "rated" ? "primary" : "secondary"}
            onClick={() => setMode("rated")}
          >
            На рейтинг
          </Button>
        </div>
      </Field>

      <Field
        label="Где"
        hint={
          `Можно выбрать до ${MAX_SPOTS} спотов. Ищем на них, а если долго никого нет — ` +
          "и на соседних, но не дальше 8 км."
        }
      >
        {selected.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {selected.map((s) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: "var(--r-md)",
                  background: "var(--bg-section)",
                }}
              >
                <MapPin size={16} color="var(--brand-orange)" style={{ flex: "none" }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="body" style={{ display: "block" }}>{s.name}</span>
                  <span className="caption" style={{ display: "block", color: "var(--fg3)" }}>{s.address}</span>
                </span>
                <button
                  type="button"
                  aria-label={`Убрать ${s.name}`}
                  onClick={() => toggleSpot(s.id)}
                  style={{ background: "transparent", border: 0, color: "var(--fg3)", cursor: "pointer", padding: 4 }}
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <Chip active={spotView === "list"} onClick={() => setSpotView("list")}>Списком</Chip>
          <Chip active={spotView === "map"} onClick={() => setSpotView("map")}>На карте</Chip>
        </div>
        {spotView === "map" ? (
          <SpotMapPicker
            spots={playable}
            selectedIds={spotIds}
            max={MAX_SPOTS}
            onToggle={toggleSpot}
          />
        ) : spotIds.length < MAX_SPOTS && (
          // `key` remounts the picker so it closes and clears after each pick.
          <SpotPicker
            key={spotIds.length}
            spots={playable}
            value={null}
            excludeIds={spotIds}
            onChange={(id) => id !== null && setSpotIds((ids) => [...ids, id])}
            emptyLabel={null}
            closedPlaceholder={spotIds.length === 0 ? "Выбери спот" : "Добавить ещё спот"}
          />
        )}
      </Field>

      <Field label="Когда">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Chip active={timeMode === "now"} onClick={() => setTimeMode("now")}>Сейчас</Chip>
          {eveningOk && (
            <Chip active={timeMode === "evening"} onClick={() => setTimeMode("evening")}>
              Сегодня вечером
            </Chip>
          )}
          <Chip active={timeMode === "any"} onClick={() => setTimeMode("any")}>Неважно</Chip>
          <Chip active={timeMode === "exact"} onClick={() => setTimeMode("exact")}>
            Выбрать время
          </Chip>
        </div>
        {timeMode === "exact" && (
          <div style={{ marginTop: 8 }}>
            <DateTimeInput value={exactAt} onChange={setExactAt} min={dayjs().toISOString()} required />
          </div>
        )}
        <span className="caption" style={{ color: "var(--fg3)" }}>
          {timeMode === "now" && "Ищем тех, кто готов сыграть в ближайшие полтора часа."}
          {timeMode === "evening" &&
            (eveningOk ? "Сегодня с 18:00 до 23:00 МСК." : "Вечер уже почти прошёл — выбери другое время.")}
          {timeMode === "any" && "Подойдёт любое время в ближайшую неделю."}
          {timeMode === "exact" && "Подберём тех, кому удобно ±2 часа от этого времени."}
        </span>
      </Field>

      {mode === "casual" ? (
        <div style={{ padding: "10px 16px" }}>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={group}
              onChange={(e) => setGroup(e.target.checked)}
              style={{ width: 20, height: 20, accentColor: "var(--brand-orange)", marginTop: 1 }}
            />
            <span>
              <span className="body" style={{ display: "block" }}>Открыт для игры компанией</span>
              <span className="caption" style={{ color: "var(--fg3)" }}>
                Позовём тебя в игру рядом, если она уже собирается, а к вам смогут присоединиться другие.
              </span>
            </span>
          </label>
        </div>
      ) : (
        <div className="caption" style={{ color: "var(--fg3)", padding: "6px 16px" }}>
          Рейтинговая игра — всегда один на один. Подберём соперника близкого уровня;
          после игры бот спросит, кто победил.
        </div>
      )}

      {state.data && !state.data.has_username && (
        <div
          className="caption"
          style={{
            margin: "8px 16px",
            padding: "10px 12px",
            borderRadius: "var(--r-md)",
            background: "var(--bg-section)",
            color: "var(--fg2)",
          }}
        >
          У тебя нет ника в Telegram — соперник не сможет написать тебе напрямую.
          Добавь его в настройках Telegram, а пока договаривайтесь на доске игры.
        </div>
      )}

      <ErrorBox error={create.error} />

      <div style={{ padding: "12px 16px" }}>
        <Button fullWidth onClick={submit} disabled={!canSubmit}>
          {create.isPending ? "Запускаем…" : "Искать"}
        </Button>
      </div>
    </div>
  );
}
